import { createReducer, on } from '@ngrx/store';
import { Document, DocumentDetail, DocumentListItem, UploadProgress, DocumentFilters } from '../models/document.models';
import * as DocumentActions from './document.actions';

/**
 * Document State Interface
 */
export interface DocumentState {
  // Documents list
  documents: DocumentListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;

  // Filters
  filters: DocumentFilters;

  // Selected document
  selectedDocument: Document | null;

  // Current document detail
  currentDocument: DocumentDetail | null;

  // Upload queue
  uploadQueue: UploadProgress[];

  // Loading states
  loading: boolean;
  loadingDocument: boolean;
  loadingDocumentDetail: boolean;
  uploading: boolean;
  deleting: boolean;

  // Error states
  error: string | null;
  uploadError: string | null;
  deleteError: string | null;
  documentDetailError: string | null;
}

/**
 * Initial State
 */
export const initialState: DocumentState = {
  documents: [],
  total: 0,
  page: 1,
  page_size: 10,
  total_pages: 0,
  has_next: false,
  has_prev: false,
  filters: {
    page: 1,
    page_size: 10
  },
  selectedDocument: null,
  currentDocument: null,
  uploadQueue: [],
  loading: false,
  loadingDocument: false,
  loadingDocumentDetail: false,
  uploading: false,
  deleting: false,
  error: null,
  uploadError: null,
  deleteError: null,
  documentDetailError: null
};

/**
 * Document Reducer
 */
export const documentReducer = createReducer(
  initialState,
  
  // Load Documents
  on(DocumentActions.loadDocuments, (state, { filters }) => {
    console.log('[Documents Reducer] loadDocuments - setting loading=true, filters:', filters);
    return {
      ...state,
      loading: true,
      error: null,
      filters: { ...state.filters, ...filters }
    };
  }),

  on(DocumentActions.loadDocumentsSuccess, (state, { data }) => {
    console.log('[Documents Reducer] loadDocumentsSuccess - setting loading=false, items count:', data.items.length);
    const pageSize = data.page_size || state.page_size;
    const items = data.items.length > pageSize ? data.items.slice(0, pageSize) : data.items;
    return {
      ...state,
      loading: false,
      documents: items,
      total: data.total,
      page: data.page,
      page_size: data.page_size,
      total_pages: data.total_pages,
      has_next: data.has_next,
      has_prev: data.has_prev,
      error: null
    };
  }),

  on(DocumentActions.loadDocumentsFailure, (state, { error }) => {
    console.log('[Documents Reducer] loadDocumentsFailure - setting loading=false, error:', error);
    return {
      ...state,
      loading: false,
      error
    };
  }),
  
  // Load Single Document
  on(DocumentActions.loadDocument, (state) => ({
    ...state,
    loadingDocument: true,
    error: null
  })),
  
  on(DocumentActions.loadDocumentSuccess, (state, { document }) => ({
    ...state,
    loadingDocument: false,
    selectedDocument: document,
    error: null
  })),
  
  on(DocumentActions.loadDocumentFailure, (state, { error }) => ({
    ...state,
    loadingDocument: false,
    error
  })),
  
  // Upload Document
  on(DocumentActions.uploadDocument, (state, { file }) => ({
    ...state,
    uploading: true,
    uploadError: null,
    uploadQueue: [
      ...state.uploadQueue,
      { file, progress: 0, status: 'pending' as const }
    ]
  })),
  
  on(DocumentActions.uploadDocumentProgress, (state, { file, progress }) => ({
    ...state,
    uploadQueue: state.uploadQueue.map(item =>
      item.file === file
        ? { ...item, progress, status: 'uploading' as const }
        : item
    )
  })),
  
  on(DocumentActions.uploadDocumentSuccess, (state, { response }) => ({
    ...state,
    uploading: false,
    uploadError: null,
    uploadQueue: state.uploadQueue.map(item =>
      item.file.name === response.filename
        ? { ...item, progress: 100, status: 'completed' as const, document_id: response.document_id }
        : item
    )
  })),
  
  on(DocumentActions.uploadDocumentFailure, (state, { file, error }) => ({
    ...state,
    uploading: false,
    uploadError: error,
    uploadQueue: state.uploadQueue.map(item =>
      item.file === file
        ? { ...item, status: 'failed' as const, error }
        : item
    )
  })),

  // Cancel Upload
  on(DocumentActions.cancelUpload, (state, { file }) => ({
    ...state,
    uploadQueue: state.uploadQueue.map(item =>
      item.file === file
        ? { ...item, status: 'cancelled' as const }
        : item
    )
  })),

  // Large File Upload
  on(DocumentActions.uploadLargeDocument, (state, { file }) => ({
    ...state,
    uploading: true,
    uploadError: null,
    uploadQueue: [
      ...state.uploadQueue,
      { file, progress: 0, status: 'pending' as const }
    ]
  })),

  on(DocumentActions.uploadLargeDocumentProgress, (state, { file, progress }) => ({
    ...state,
    uploadQueue: state.uploadQueue.map(item =>
      item.file === file
        ? { ...item, progress, status: 'uploading' as const }
        : item
    )
  })),

  on(DocumentActions.uploadLargeDocumentSuccess, (state, { file, documentId }) => ({
    ...state,
    uploading: false,
    uploadError: null,
    uploadQueue: state.uploadQueue.map(item =>
      item.file === file
        ? { ...item, progress: 100, status: 'completed' as const, document_id: documentId }
        : item
    )
  })),

  on(DocumentActions.uploadLargeDocumentFailure, (state, { file, error }) => ({
    ...state,
    uploading: false,
    uploadError: error,
    uploadQueue: state.uploadQueue.map(item =>
      item.file === file
        ? { ...item, status: 'failed' as const, error }
        : item
    )
  })),

  // Delete Document
  on(DocumentActions.deleteDocument, (state) => ({
    ...state,
    deleting: true,
    deleteError: null
  })),

  on(DocumentActions.deleteDocumentSuccess, (state, { documentId }) => ({
    ...state,
    deleting: false,
    documents: state.documents.filter(doc => doc._id !== documentId),
    total: state.total - 1,
    deleteError: null
  })),

  on(DocumentActions.deleteDocumentFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    deleteError: error
  })),

  // Bulk Delete
  on(DocumentActions.bulkDeleteDocuments, (state) => ({
    ...state,
    deleting: true,
    deleteError: null
  })),

  on(DocumentActions.bulkDeleteDocumentsSuccess, (state, { documentIds }) => ({
    ...state,
    deleting: false,
    documents: state.documents.filter(doc => !documentIds.includes(doc._id)),
    total: state.total - documentIds.length,
    deleteError: null
  })),

  on(DocumentActions.bulkDeleteDocumentsFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    deleteError: error
  })),

  // Update Filters
  on(DocumentActions.updateFilters, (state, { filters }) => ({
    ...state,
    filters: { ...state.filters, ...filters }
  })),

  // Clear State
  on(DocumentActions.clearDocuments, () => initialState),

  on(DocumentActions.clearSelectedDocument, (state) => ({
    ...state,
    selectedDocument: null
  })),

  on(DocumentActions.clearUploadQueue, (state) => ({
    ...state,
    uploadQueue: []
  })),

  // Load Document Detail
  on(DocumentActions.loadDocumentDetail, (state) => ({
    ...state,
    loadingDocumentDetail: true,
    documentDetailError: null
  })),

  on(DocumentActions.loadDocumentDetailSuccess, (state, { document }) => ({
    ...state,
    loadingDocumentDetail: false,
    currentDocument: { ...document, id: document._id },
    documentDetailError: null
  })),

  on(DocumentActions.loadDocumentDetailFailure, (state, { error }) => ({
    ...state,
    loadingDocumentDetail: false,
    currentDocument: null,
    documentDetailError: error
  }))
);

